import { Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { RegulationFetch } from '../../features/regulation.feature';
import { useAppDispatch, useAppSelector } from '../../hooks';
import RegulationCards from '../regulation/regulation-cards.component';
import { Loading } from '../shared';
import { isUserDateOnOrAfterShipAge } from '../shared/criteria-date.util';
import TrackerCriteriaResultsFilter from './tracker-criteria-results-filter.component';
import { IAmendment, IRegulation } from '../../contracts/amendment.contract';

const TrackerCriteriaResults = () => {
    const dispatch = useAppDispatch();
    const criteria = useAppSelector((state) => state.Criteria.TrackerCriteria);
    const regulations = useAppSelector((state) => state.Regulation.ByCriteria.Regulations);
    const isLoading = useAppSelector((state) => state.Regulation.ByCriteria.IsLoading);

    // Fetch data only once when the component mounts
    useEffect(() => {
        if (!criteria) return;
        dispatch(RegulationFetch.GetByCriteria(criteria));
    }, [criteria, dispatch]);

    // Helper function to extract numeric value from IAmendmentRange
    const getAmendmentRangeValue = (range: any): number => {
        if (!range) return 0;

        // Try to get the Value field first
        if (range.Value !== null && range.Value !== undefined && range.Value !== '') {
            const value = parseFloat(range.Value);
            return isNaN(value) ? 0 : value;
        }

        // If Value is null/empty, check LowerBound
        if (range.LowerBound && range.LowerBound.Value !== null && range.LowerBound.Value !== undefined && range.LowerBound.Value !== '') {
            const value = parseFloat(range.LowerBound.Value);
            return isNaN(value) ? 0 : value;
        }

        // Default to 0 if blank
        return 0;
    };

    // DateOfAdoption from API: "08/06/2023" (dd/MM/yyyy). Missing/invalid → null (sort last).
    const parseAdoptionDate = (value?: string): Date | null => {
        if (!value?.trim()) return null;
        const [day, month, year] = value.trim().split('/').map(Number);
        if (!day || !month || !year) return null;
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
        return date;
    };

    const sortAmendmentsByAdoptionDate = (amendments: IAmendment[]): IAmendment[] => {
        return [...amendments].sort((a, b) => {
            const aDate = parseAdoptionDate(a.DateOfAdoption);
            const bDate = parseAdoptionDate(b.DateOfAdoption);
            if (aDate && bDate) return bDate.getTime() - aDate.getTime();
            if (aDate) return -1;
            if (bDate) return 1;
            return 0;
        });
    };

    const sortRegulationsByAdoptionDate = (regs: IRegulation[]): IRegulation[] => {
        const withSortedAmendments = regs.map((regulation) => ({
            ...regulation,
            Amendments: sortAmendmentsByAdoptionDate(regulation.Amendments ?? [])
        }));

        return withSortedAmendments.sort((a, b) => {
            const aDate = parseAdoptionDate(a.Amendments[0]?.DateOfAdoption);
            const bDate = parseAdoptionDate(b.Amendments[0]?.DateOfAdoption);
            if (aDate && bDate) {
                const byDate = bDate.getTime() - aDate.getTime();
                if (byDate !== 0) return byDate;
            } else if (aDate) {
                return -1;
            } else if (bDate) {
                return 1;
            }
            return (a.RegulationName || '').localeCompare(b.RegulationName || '');
        });
    };

    // Filter regulations based on criteria
    const filteredRegulations = useMemo(() => {
        if (!regulations) return regulations;

        // Apply filters
        const filtered = regulations
            .map((regulation: IRegulation) => {
                // Filter amendments based on all criteria
                const filteredAmendments = regulation.Amendments.filter((amendment) => {
                    // IGF Code Applicable toggle: Off = exclude IGF; On = show IGF + non-IGF
                    const regulationText = amendment.Regulation || '';
                    const containsIGF = regulationText.toUpperCase().includes('IGF');

                    if (criteria.IGFCode === false && containsIGF) {
                        return false;
                    }

                    // Construction Date filters: OR within category (Contract / Keel Lay / Delivery)
                    // Keep amendment if it matches any selected type with userDate >= ShipAgeDate
                    const hasConstructionDateFilter = !!criteria.ContractBeforeDate || !!criteria.KeelLayBeforeDate || !!criteria.DeliveryBeforeDate;

                    if (hasConstructionDateFilter) {
                        const complianceType = (amendment.ComplianceDateType || '').toUpperCase();

                        const matchesContract =
                            !!criteria.ContractBeforeDate &&
                            complianceType === 'CONTRACT' &&
                            isUserDateOnOrAfterShipAge(criteria.ContractBeforeDate, amendment.ShipAgeDate);

                        const matchesKeelLay =
                            !!criteria.KeelLayBeforeDate &&
                            complianceType === 'KEEL_LAY' &&
                            isUserDateOnOrAfterShipAge(criteria.KeelLayBeforeDate, amendment.ShipAgeDate);

                        const matchesDelivery =
                            !!criteria.DeliveryBeforeDate &&
                            complianceType === 'DELIVERY' &&
                            isUserDateOnOrAfterShipAge(criteria.DeliveryBeforeDate, amendment.ShipAgeDate);

                        if (!matchesContract && !matchesKeelLay && !matchesDelivery) {
                            return false;
                        }
                    }

                    // Vessel Size filters
                    // Logic: Amendment is applicable if user's value >= amendment's vessel size value
                    // If amendment's vessel size is blank, treat it as 0

                    // Gross Tonnage (GT) filter
                    if (criteria.GrossTonnage !== null && criteria.GrossTonnage !== undefined) {
                        const amendmentGT = getAmendmentRangeValue(amendment.GrossTonnage);
                        if (criteria.GrossTonnage < amendmentGT) return false;
                    }

                    // Number of Passengers filter
                    if (criteria.NumberOfPassengers !== null && criteria.NumberOfPassengers !== undefined) {
                        const amendmentPassengers = getAmendmentRangeValue(amendment.Passengers);
                        if (criteria.NumberOfPassengers < amendmentPassengers) return false;
                    }

                    // Deadweight (DWT) filter
                    if (criteria.Deadweight !== null && criteria.Deadweight !== undefined) {
                        const amendmentDWT = getAmendmentRangeValue(amendment.DWT);
                        if (criteria.Deadweight < amendmentDWT) return false;
                    }

                    // Load Line Length (LLL/ILL) filter
                    if (criteria.LoadLineLength !== null && criteria.LoadLineLength !== undefined) {
                        const amendmentILL = getAmendmentRangeValue(amendment.ILL);
                        if (criteria.LoadLineLength < amendmentILL) return false;
                    }

                    // Length Overall (LOA) filter
                    if (criteria.LengthOverall !== null && criteria.LengthOverall !== undefined) {
                        const amendmentLOA = getAmendmentRangeValue(amendment.LOA);
                        if (criteria.LengthOverall < amendmentLOA) return false;
                    }

                    return true;
                });

                // Return regulation with filtered amendments
                return {
                    ...regulation,
                    Amendments: filteredAmendments
                };
            })
            .filter((regulation: IRegulation) => regulation.Amendments.length > 0); // Remove regulations with no amendments

        return sortRegulationsByAdoptionDate(filtered);
    }, [
        regulations,
        criteria.IGFCode,
        criteria.ContractBeforeDate,
        criteria.KeelLayBeforeDate,
        criteria.DeliveryBeforeDate,
        criteria.GrossTonnage,
        criteria.NumberOfPassengers,
        criteria.Deadweight,
        criteria.LoadLineLength,
        criteria.LengthOverall
    ]);

    return (
        <Stack direction="column" alignItems="center" justifyContent="center" spacing={2}>
            <TrackerCriteriaResultsFilter />
            {isLoading ? (
                <Loading />
            ) : (
                <RegulationCards regulations={filteredRegulations ?? []} showIgfCodeToggle />
            )}
        </Stack>
    );
};

export default TrackerCriteriaResults;
